import React, { createContext, useContext, useState, useCallback } from 'react';
import { FullScreenLoader } from './FullScreenLoader';

interface GlobalLoadingState {
  isVisible: boolean;
  variant: 'basic' | 'with-logo' | 'with-progress' | 'with-text';
  text: string;
  progress?: number;
  context: string;
}

interface GlobalLoadingContextType {
  showFullScreenLoader: (options?: {
    variant?: GlobalLoadingState['variant'];
    text?: string;
    progress?: number;
    context?: string;
  }) => void;
  hideFullScreenLoader: (context?: string) => void;
  updateProgress: (progress: number, text?: string) => void;
}

const GlobalLoadingContext = createContext<GlobalLoadingContextType | undefined>(undefined);

/**
 * Global loading provider for app-wide loading states
 * Manages full-screen loading overlays with proper coordination
 */
export function GlobalLoadingProvider({ children }: { children: React.ReactNode }) {
  const [loadingState, setLoadingState] = useState<GlobalLoadingState>({
    isVisible: false,
    variant: 'basic',
    text: 'Loading...',
    context: 'default'
  });

  const showFullScreenLoader = useCallback((options: {
    variant?: GlobalLoadingState['variant'];
    text?: string;
    progress?: number;
    context?: string;
  } = {}) => {
    setLoadingState({
      isVisible: true,
      variant: options.variant || 'basic',
      text: options.text || 'Loading...',
      progress: options.progress,
      context: options.context || 'default'
    });
  }, []);

  const hideFullScreenLoader = useCallback((context?: string) => {
    setLoadingState(prev => {
      // Only hide if context matches or no specific context provided
      if (!context || prev.context === context) {
        return {
          ...prev,
          isVisible: false
        };
      }
      return prev;
    });
  }, []);

  const updateProgress = useCallback((progress: number, text?: string) => {
    setLoadingState(prev => ({
      ...prev,
      progress,
      text: text || prev.text
    }));
  }, []);

  const contextValue: GlobalLoadingContextType = {
    showFullScreenLoader,
    hideFullScreenLoader,
    updateProgress
  };

  return (
    <GlobalLoadingContext.Provider value={contextValue}>
      {children}
      
      {/* Global full-screen loader overlay */}
      {loadingState.isVisible && (
        <FullScreenLoader
          variant={loadingState.variant}
          text={loadingState.text}
          progress={loadingState.progress}
        />
      )}
    </GlobalLoadingContext.Provider>
  );
}

/**
 * Hook to access global loading functionality
 */
export function useGlobalLoading() {
  const context = useContext(GlobalLoadingContext);
  if (context === undefined) {
    throw new Error('useGlobalLoading must be used within a GlobalLoadingProvider');
  }
  return context;
}